function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    if (enumerableOnly) symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    });
    keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};

    if (i % 2) {
      ownKeys(Object(source), true).forEach(function (key) {
        _defineProperty(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys(Object(source)).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }

  return target;
}

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

import React, { unstable_batchedUpdates } from 'react';
var useState = React.useState,
    useRef = React.useRef,
    useCallback = React.useCallback,
    useEffect = React.useEffect;
import { useThrottleCallback } from '@react-hook/throttle';
var initialState = {
  x: null,
  y: null,
  pageX: null,
  pageY: null,
  clientX: null,
  clientY: null,
  screenX: null,
  screenY: null,
  elementWidth: null,
  elementHeight: null,
  isOver: false,
  isDown: false,
  isTouch: false
};
var batchUpdates = typeof unstable_batchedUpdates === 'function' ? unstable_batchedUpdates : function (fn) {
  return fn();
};

function _ref7(prev) {
  return _objectSpread({}, prev, {
    isDown: false
  });
}

export var useMousePosition = function useMousePosition() {
  var enterDelay = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 0;
  var leaveDelay = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
  var fps = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 30;

  var _ref_0 = useState(initialState);

  var setState = _ref_0[1];
  var state = _ref_0[0];

  var _ref_1 = useState(false);

  var setEntered = _ref_1[1];
  var entered = _ref_1[0];
  var touchEnded = useRef(false);

  var _ref_2 = useState(false);

  var setActive = _ref_2[1];
  var active = _ref_2[0];

  var _ref_3 = useState(null);

  var setElement = _ref_3[1];
  var element = _ref_3[0];
  var onMoveCallback = useThrottleCallback(useCallback(function (e) {
    var event;
    var isTouch = typeof e.touches !== 'undefined';

    if (isTouch) {
      event = e.touches[0];
    } else {
      event = e;
    }

    var _event = event,
        clientX = _event.clientX,
        clientY = _event.clientY,
        screenX = _event.screenX,
        screenY = _event.screenY,
        _event$pageX = _event.pageX,
        pageX = _event$pageX === void 0 ? 0 : _event$pageX,
        _event$pageY = _event.pageY,
        pageY = _event$pageY === void 0 ? 0 : _event$pageY;
    var rect = element.getBoundingClientRect();
    var x = pageX - rect.left - (window.pageXOffset || window.scrollX);
    var y = pageY - rect.top - (window.pageYOffset || window.scrollY); // shims a mouseleave event for touch devices

    if (isTouch && (x < 0 || y < 0 || x > rect.width || y > rect.height)) {
      setEntered(false);
      touchEnded.current = true;
      return;
    }

    setState(function (prev) {
      return {
        x: x,
        y: y,
        pageX: pageX,
        pageY: pageY,
        clientX: clientX,
        clientY: clientY,
        screenX: screenX,
        screenY: screenY,
        elementWidth: rect.width,
        elementHeight: rect.height,
        isOver: true,
        isDown: prev.isDown,
        isTouch: isTouch
      };
    });
  }, [element]), fps, true);

  function _ref(evt) {
    var isTouch = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
    return setState(function (prev) {
      return _objectSpread({}, prev, {
        isDown: true,
        isTouch: isTouch
      });
    });
  }

  function _ref3(e) {
    function _ref2() {
      setEntered(true);
      onMoveCallback(e);
    }

    if (!touchEnded.current) {
      batchUpdates(_ref2);
    }
  }

  function _ref4() {
    return setEntered(false);
  }

  function _ref6(e) {
    function _ref5() {
      setEntered(true);
      onMoveCallback(e);
    }

    if (!touchEnded.current) {
      onMoveCallback(e);
      batchUpdates(_ref5);
    }
  }

  function _ref8() {
    return setState(_ref7);
  }

  function _ref9(e) {
    touchEnded.current = false;
    onMoveCallback(e);
  }

  useEffect(function () {
    if (element !== null) {
      var setDown = _ref;
      var onMove = _ref3;
      var onLeave = _ref4;
      var onDown = _ref6;
      var onUp = _ref8;

      var onTouchStart = function onTouchStart(e) {
        touchEnded.current = false;
        setDown(e, true);
        onDown(e);
      };

      var onTouchMove = _ref9;

      function _ref10() {
        setEntered(false);
        onUp();
      }

      var onTouchEnd = function onTouchEnd() {
        touchEnded.current = true;
        batchUpdates(_ref10);
      };

      var addEvent = element.addEventListener.bind(element);
      addEvent('mouseenter', onMove);
      addEvent('mousemove', onMove);
      addEvent('mouseleave', onLeave);
      addEvent('mousedown', onDown);
      window.addEventListener('mousedown', setDown);
      window.addEventListener('mouseup', onUp);
      addEvent('touchstart', onTouchStart);
      addEvent('touchmove', onTouchMove);
      addEvent('touchend', onTouchEnd);
      addEvent('touchcancel', onTouchEnd);
      return function () {
        var removeEvent = element.removeEventListener.bind(element);
        removeEvent('mouseenter', onMove);
        removeEvent('mousemove', onMove);
        removeEvent('mouseleave', onLeave);
        removeEvent('mousedown', onDown);
        window.removeEventListener('mousedown', setDown);
        window.removeEventListener('mouseup', onUp);
        removeEvent('touchstart', onTouchStart);
        removeEvent('touchmove', onTouchMove);
        removeEvent('touchend', onTouchEnd);
        removeEvent('touchcancel', onTouchEnd);
      };
    }
  }, [element, enterDelay, leaveDelay, onMoveCallback]);

  function _ref11() {
    return setActive(true);
  }

  function _ref12() {
    return setActive(false);
  }

  useEffect(function () {
    if (entered) {
      if (enterDelay) {
        var timeout = setTimeout(_ref11, enterDelay);
        return function () {
          return clearTimeout(timeout);
        };
      }

      setActive(true);
    } else {
      if (leaveDelay) {
        var _timeout = setTimeout(_ref12, leaveDelay);

        return function () {
          return clearTimeout(_timeout);
        };
      }

      setActive(false);
    }
  }, [entered, enterDelay, leaveDelay]);
  return [active ? state : initialState, setElement];
};
export default useMousePosition;